import { useAuth0 } from "@auth0/auth0-react";
import { Owner } from "gql/generated";
import { useCallback, useEffect, useState } from "react";
import { useAppVersionContext } from "useAppVersionContext";
import { useAppContext } from "useAppContext";
import { useLoggedInAs } from "./useLoggedInAs";

export const useHelpScout = () => {
  // Get the dbUser, auth0User, and loggedInAs values from their contexts
  // Since NOT all parts of the app will be wrapped in the respective contexts,
  // we need to use try/catch blocks to safely access the values
  const dbUser = useSaveDbUser();
  const auth0User = useSaveAuth0User();
  const loggedInAs = useSaveLoggedInAs();
  const currentVersion = useSaveAppVersion();

  // Store the beacon values
  const [beaconValues, setBeaconValues] = useState<
    Omit<InitializeBeaconProps, "subject">
  >({});

  // When the dbUser, auth0User, or loggedInAs values change, update the beacon values
  useEffect(
    () =>
      setBeaconValues(prev => ({
        name: dbUser
          ? `${dbUser.firstName} ${dbUser.lastName}`
          : (auth0User?.nickname ?? prev.name ?? ""),
        email: dbUser?.email ?? auth0User?.email ?? prev.email ?? "",
        userId: dbUser?._id ?? "",
        auth0Id: auth0User?.sub ?? "",
        loggedInAs: loggedInAs ?? null,
        userOperatorIds:
          dbUser && dbUser.access?.operators
            ? dbUser.access.operators.map(op => op?.entityId)
            : [],
        userOrganizationIds:
          dbUser && dbUser.access?.organizations
            ? dbUser.access.organizations.map(org => org?.entityId)
            : [],
        userStorefrontIds:
          dbUser && dbUser.access?.storefronts
            ? dbUser.access.storefronts.map(sf => sf?.entityId)
            : [],
        whereaboutsVersion: currentVersion ?? "",
      })),
    [dbUser, auth0User, loggedInAs, currentVersion],
  );

  // When the beacon values change, update the Beacon session data
  useEffect(() => updateBeaconSessionData(beaconValues), [beaconValues]);

  /**
   * When the user clicks the Beacon icon, open the Beacon window
   * - Attaches the HelpScout Beacon script to the DOM
   * - Updates the beacon values with the user data (if provided)
   * - Initializes the Beacon SDK, prefills the form, and sets the session data
   * - Adds an event listener for when the Beacon window is closed
   * - Opens the Beacon window
   * @param subject - The subject of the help request
   * @param userData - The user data (if dbUser or auth0User is not available, use this)
   */
  const handleBeaconClick = useCallback(
    ({
      subject,
      userData,
    }: {
      subject?: string;
      userData?: { email?: string; name?: string };
    }) => {
      attachBeaconScript();

      // If userData, has email OR name, update the beacon values
      if (userData?.email || userData?.name) {
        // Temporary update to the beacon values
        // This is so the values are correct in the call below to `setInitializeBeaconValues`
        beaconValues.email = userData?.email ?? beaconValues.email;
        beaconValues.name = userData?.name ?? beaconValues.name;
        // The real update to the beacon values
        setBeaconValues({ ...beaconValues });
      }

      // Initialize the Beacon SDK, prefill, and set session data
      setInitializeBeaconValues({
        ...beaconValues,
        subject: subject ?? "Need help with Whereabouts",
      });

      // Add event listener for when Beacon is closed
      window.Beacon("on", "close", () => {
        window.Beacon("destroy");
      });

      // Open the Beacon window
      window.Beacon("open");
    },
    [beaconValues],
  );

  return {
    handleBeaconClick,
  };
};

/**
 * Attaches the Beacon script to the DOM and initializes the Beacon SDK
 * @returns
 */
const attachBeaconScript = () => {
  if (window.Beacon) return;

  const script = document.createElement("script");
  script.id = "beacon";
  script.text = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`;
  document.head.appendChild(script);
};

type InitializeBeaconProps = {
  name?: string;
  email?: string;
  subject?: string;
  userId?: string;
  auth0Id?: string;
  userOperatorIds?: string[];
  userOrganizationIds?: string[];
  userStorefrontIds?: string[];
  loggedInAs?: Owner | null;
  whereaboutsVersion?: string;
};

/**
 * Sets the initial Beacon values for when the Beacon is opened
 * @param name
 * @param email
 * @param subject
 * @param userId
 * @param auth0Id
 * @param userOperatorIds
 * @param userOrganizationIds
 * @param userStorefrontIds
 * @param loggedInAs
 * @param whereaboutsVersion
 */
const setInitializeBeaconValues = ({
  name = "",
  email = "",
  subject = "Need help with Whereabouts",
  userId = "",
  auth0Id = "",
  userOperatorIds = [],
  userOrganizationIds = [],
  userStorefrontIds = [],
  loggedInAs = null,
  whereaboutsVersion = "",
}: InitializeBeaconProps) => {
  window.Beacon("init", import.meta.env.VITE_HELPSCOUT_BEACON_INIT);
  window.Beacon("prefill", {
    name,
    email,
    subject,
    text: "",
  });
  window.Beacon("identify", {
    name,
    email,
  });

  updateBeaconSessionData({
    name,
    email,
    userId,
    auth0Id,
    userOperatorIds,
    userOrganizationIds,
    userStorefrontIds,
    loggedInAs,
    whereaboutsVersion,
  });
};

/**
 * Updates the Beacon session values
 * - This is useful if the user happens to change pages with the Beacon open
 * @param name
 * @param email
 * @param userId
 * @param auth0Id
 * @param userOperatorIds
 * @param userOrganizationIds
 * @param userStorefrontIds
 * @param loggedInAs
 * @param whereaboutsVersion
 */
const updateBeaconSessionData = ({
  name = "",
  email = "",
  userId = "",
  auth0Id = "",
  userOperatorIds = [],
  userOrganizationIds = [],
  userStorefrontIds = [],
  loggedInAs = null,
  whereaboutsVersion = "",
}: InitializeBeaconProps) => {
  if (!window.Beacon) return;

  window.Beacon("session-data", {
    userId,
    auth0Id,
    email,
    name,
    userOperatorIds: userOperatorIds.join(","),
    userOrganizationIds: userOrganizationIds.join(","),
    userStorefrontIds: userStorefrontIds.join(","),
    ...(loggedInAs
      ? {
          loggedInAsType: loggedInAs.type,
          loggedInAsEntityId: loggedInAs.entityId,
        }
      : {}),
    url: window.location.href,
    ...(whereaboutsVersion ? { whereaboutsVersion } : {}),
  });
};

const useSaveDbUser = () => {
  try {
    return useAppContext().dbUser;
  } catch {
    return undefined;
  }
};

const useSaveAuth0User = () => {
  try {
    return useAuth0().user;
  } catch {
    return undefined;
  }
};

const useSaveLoggedInAs = () => {
  try {
    return useLoggedInAs();
  } catch {
    return undefined;
  }
};

const useSaveAppVersion = () => {
  try {
    return useAppVersionContext().currentVersion;
  } catch {
    return undefined;
  }
};
